export enum WorkflowType {
  ApprovalChain = 'approval_chain',
  ReviewChain = 'review_chain',
  NotificationChain = 'notification_chain'
}

export enum WorkflowStatus {
  Draft = 'draft',
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived'
}

export enum ApprovalStepType {
  UserRole = 'user_role',
  SpecificUser = 'specific_user',
  AnyFromGroup = 'any_from_group',
  AllFromGroup = 'all_from_group'
}

export interface ApprovalStep {
  id: string;
  order: number;
  name: string;
  description?: string;
  type: ApprovalStepType;
  
  // Role-based approval
  requiredRoles?: string[];
  
  // User-based approval
  requiredUserIds?: string[];
  
  // Group-based approval
  distributionGroupId?: string;
  requireAllFromGroup?: boolean;
  
  // Timing
  timeoutHours?: number;
  escalationUserIds?: string[];
  
  // Conditions
  isOptional?: boolean;
  conditions?: ApprovalCondition[];
}

export interface ApprovalCondition {
  id: string;
  field: string; // e.g., 'sessionType', 'templateCategory', 'priority'
  operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than';
  value: string;
}

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  type: WorkflowType;
  status: WorkflowStatus;
  
  // Steps
  steps: ApprovalStep[];
  
  // Triggers
  triggerEvents: string[]; // e.g., ['session_created', 'template_updated']
  triggerConditions?: ApprovalCondition[];
  
  // Settings
  allowParallelApproval?: boolean;
  requireSequentialApproval?: boolean;
  autoApproveAfterTimeout?: boolean;
  
  // Metadata
  companyId: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  lastUsedAt?: string;
  
  // Statistics
  totalExecutions?: number;
  averageCompletionTime?: number;
  successRate?: number;
}

export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  type: WorkflowType;
  steps: Omit<ApprovalStep, 'id'>[];
  triggerEvents: string[];
  triggerConditions?: Omit<ApprovalCondition, 'id'>[];
  allowParallelApproval?: boolean;
  requireSequentialApproval?: boolean;
  autoApproveAfterTimeout?: boolean;
}

export interface UpdateWorkflowRequest {
  id: string;
  name?: string;
  description?: string;
  status?: WorkflowStatus;
  steps?: ApprovalStep[];
  triggerEvents?: string[];
  triggerConditions?: ApprovalCondition[];
  allowParallelApproval?: boolean;
  requireSequentialApproval?: boolean;
  autoApproveAfterTimeout?: boolean;
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  workflow: Workflow;
  
  // Context
  triggeredBy: string;
  triggerEvent: string;
  relatedEntityId: string;
  relatedEntityType: string;
  
  // Progress
  currentStepIndex: number;
  completedSteps: string[];
  pendingSteps: string[];
  
  // Status
  status: 'pending' | 'in_progress' | 'completed' | 'rejected' | 'timeout' | 'cancelled';
  startedAt: string;
  completedAt?: string;
  
  // Results
  finalDecision?: 'approved' | 'rejected';
  rejectionReason?: string;
  notes?: string;
}

export interface WorkflowFilters {
  status?: WorkflowStatus[];
  type?: WorkflowType[];
  search?: string;
  createdBy?: string[];
}

export interface WorkflowListResponse {
  workflows: Workflow[];
  totalCount: number;
  hasMore: boolean;
}
